import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import { authService } from '../../../../services/auth';
import './AvatarUpload.css';

const AvatarUpload = ({ user, onAvatarUpdate, onError }) => {
  const { t } = useTranslation(['profile', 'common']);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Handle file selection
  const onFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      onError(t('profile:avatar.errors.invalid_type'));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      onError(t('profile:avatar.errors.file_too_large'));
      return;
    }

    setSelectedFile(file);

    // Read file and show cropper
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result);
      setShowCropper(true);
    });
    reader.readAsDataURL(file);
  }, [onError, t]);

  // Handle crop complete
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile || !croppedAreaPixels) return;

    setUploading(true);
    try {
      await authService.uploadAvatar(selectedFile, croppedAreaPixels);
      
      // Refresh user data
      const updatedUser = await authService.fetchCurrentUser();
      onAvatarUpdate(updatedUser);
      
      // Reset state
      setShowCropper(false);
      setImageSrc(null);
      setSelectedFile(null);
      setCroppedAreaPixels(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (error) {
      onError(error.response?.data?.message || t('profile:avatar.errors.upload_failed'));
    } finally {
      setUploading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setShowCropper(false);
    setImageSrc(null);
    setSelectedFile(null);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // Handle delete avatar
  const handleDelete = async () => {
    if (!window.confirm(t('profile:avatar.confirm_delete'))) return;

    setUploading(true);
    try {
      await authService.deleteAvatar();
      
      // Refresh user data
      const updatedUser = await authService.fetchCurrentUser();
      onAvatarUpdate(updatedUser);
    } catch (error) {
      onError(error.response?.data?.message || t('profile:avatar.errors.delete_failed'));
    } finally {
      setUploading(false);
    }
  };

  // Get avatar URL
  const avatarUrl = user?.profile_picture 
    ? authService.getAvatarUrl(user.id) 
    : null;

  return (
    <div className="avatar-upload-container">
      <div className="avatar-preview">
        {avatarUrl ? (
          <img 
            src={avatarUrl} 
            alt={user.username} 
            className="avatar-image"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className="avatar-placeholder" 
          style={{ display: avatarUrl ? 'none' : 'flex' }}
        >
          <span className="avatar-initials">
            {user?.username?.charAt(0).toUpperCase() || '?'}
          </span>
        </div>
      </div>

      <div className="avatar-actions">
        <label htmlFor="avatar-input" className="btn btn-primary btn-sm">
          {t('profile:avatar.upload_button')}
        </label>
        <input
          id="avatar-input"
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={onFileChange}
          style={{ display: 'none' }}
          disabled={uploading}
        />
        
        {avatarUrl && (
          <button
            onClick={handleDelete}
            className="btn btn-danger btn-sm"
            disabled={uploading}
          >
            {t('profile:avatar.remove_button')}
          </button>
        )}
      </div>

      {/* Cropper Modal */}
      {showCropper && (
        <div className="cropper-modal">
          <div className="cropper-content">
            <h3>{t('profile:avatar.crop_title')}</h3>
            <p className="cropper-instructions">
              {t('profile:avatar.crop_instructions')}
            </p>
            
            <div className="cropper-container">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                cropShape="round"
                showGrid={false}
              />
            </div>

            <div className="zoom-controls">
              <label>{t('profile:avatar.zoom_label')}</label>
              <input
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="Zoom"
                onChange={(e) => setZoom(e.target.value)}
                className="zoom-slider"
              />
            </div>

            <div className="cropper-actions">
              <button
                onClick={handleCancel}
                className="btn btn-secondary"
                disabled={uploading}
              >
                {t('common:buttons.cancel')}
              </button>
              <button
                onClick={handleUpload}
                className="btn btn-primary"
                disabled={uploading}
              >
                {uploading ? t('profile:avatar.uploading') : t('profile:avatar.save_button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvatarUpload;